import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  ChevronRight,
  Clock3,
  Flag,
  GitCompareArrows,
  LoaderCircle,
  MapPin,
  Shield,
  Trophy,
  Users,
} from "lucide-react";

import RiderFlag from "@/components/RiderFlag";
import TeamLogo from "@/components/TeamLogo";
import { Skeleton } from "@/components/ui/skeleton";
import { useAccount } from "@/queries/account";
import { formatSvDate, formatSvTime } from "@/utils/date";
import "@/styles/native-series-v3.css";

function normalizeTeamName(value) {
  return String(value || "")
    .toLocaleLowerCase("sv-SE")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function compactTeamName(value) {
  const normalized = normalizeTeamName(value);
  if (normalized === "rospiggarna hallstavik") return "Rospiggarna";
  if (normalized === "piraterna motala") return "Piraterna";
  return String(value || "");
}

function isTeamHome(match, team) {
  return String(match?.home_team_id || "") === String(team?.id || "")
    || normalizeTeamName(match?.home_team) === normalizeTeamName(team?.name)
    || normalizeTeamName(match?.home_team_name_canonical) === normalizeTeamName(team?.name);
}

function teamName(match, side) {
  return compactTeamName(
    match?.[`${side}_team_display`]
    || match?.[`${side}_team_name_canonical`]
    || match?.[`${side}_team`]
    || "Motståndare"
  );
}

function opponentName(match, team) {
  return isTeamHome(match, team) ? teamName(match, "away") : teamName(match, "home");
}

function outcomeFor(match, team) {
  const homeScore = Number(match?.home_score);
  const awayScore = Number(match?.away_score);
  if (!Number.isFinite(homeScore) || !Number.isFinite(awayScore)) return null;
  const teamScore = isTeamHome(match, team) ? homeScore : awayScore;
  const opponentScore = isTeamHome(match, team) ? awayScore : homeScore;
  if (teamScore > opponentScore) return "V";
  if (teamScore < opponentScore) return "F";
  return "O";
}

function formatAverage(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(3) : "–";
}

function NativeSeriesSkeleton({ detail = false }) {
  return (
    <div className="native-v3-series-loading" data-detail={detail ? "true" : "false"} aria-label={detail ? "Laddar lagsida" : "Laddar lag"}>
      {detail ? (
        <>
          <Skeleton className="native-v3-series-detail-profile-skeleton" />
          <Skeleton className="native-v3-series-detail-match-skeleton" />
          <Skeleton className="native-v3-series-detail-results-skeleton" />
          <Skeleton className="native-v3-series-detail-roster-skeleton" />
        </>
      ) : Array.from({ length: 8 }).map((_, index) => (
        <Skeleton className="native-v3-series-team-row-silhouette" key={index} />
      ))}
    </div>
  );
}

function SeriesOverview({ season, teams, loading, error }) {
  const { data: account } = useAccount();
  const favoriteId = String(account?.favorite_team_id || "");
  const rounds = teams.length > 1 ? (teams.length - 1) * 2 : 0;

  return (
    <div className="native-v3-series-overview">
      <header className="native-v3-series-hero">
        <div className="native-v3-series-track" aria-hidden="true" />
        <span className="native-v3-series-kicker">Bauhausligan</span>
        <div className="native-v3-series-title-row">
          <div>
            <h1 className="native-v3-display">Serien</h1>
            <p>Åtta lag. En mästare.</p>
          </div>
          <span className="native-v3-series-season">Säsong {season}</span>
        </div>
        <div className="native-v3-series-facts">
          <span><Flag aria-hidden="true" /> Grundserie</span>
          {rounds ? <span>{rounds} omgångar</span> : null}
          <span>{loading ? <Skeleton className="h-3 w-10" /> : `${teams.length} lag`}</span>
        </div>
      </header>

      <section className="native-v3-series-team-section">
        <div className="native-v3-series-section-title">
          <span>Lagen</span>
          <h2 className="native-v3-display">Lagen {season}</h2>
        </div>
        {error ? <p className="native-v3-series-message">Kunde inte ladda lagen just nu.</p> : null}
        {loading ? <NativeSeriesSkeleton /> : null}
        {!loading && !error ? (
          <div className="native-v3-series-team-list">
            {teams.map((team, index) => {
              const position = team.standing?.position ?? index + 1;
              const favorite = favoriteId && String(team.id) === favoriteId;
              return (
                <Link key={team.id} to={`/serie/lag/${team.id}`} className="native-v3-series-team-row">
                  <span className="native-v3-series-team-rank">{position}</span>
                  <TeamLogo teamName={compactTeamName(team.name)} size={48} fallback={<Shield aria-hidden="true" />} />
                  <span className="native-v3-series-team-copy">
                    <strong className="native-v3-display">{compactTeamName(team.name)}</strong>
                    <small>{team.city || team.arena || "Bauhausligan"}</small>
                  </span>
                  {favorite ? <span className="native-v3-series-favorite" aria-label="Ditt favoritlag">★</span> : null}
                  <ChevronRight aria-hidden="true" />
                </Link>
              );
            })}
          </div>
        ) : null}
      </section>
    </div>
  );
}

function TeamProfile({ team, standing, season, matchSummary, riderCount, onRoster }) {
  const scored = Number.isFinite(Number(standing?.points_for)) ? Number(standing.points_for) : matchSummary.scored;
  const conceded = Number.isFinite(Number(standing?.points_against)) ? Number(standing.points_against) : matchSummary.conceded;
  const diff = scored - conceded;

  return (
    <header className="native-v3-team-profile">
      <div className="native-v3-series-track" aria-hidden="true" />
      <div className="native-v3-team-profile-nav">
        <Link to="/serie"><ArrowLeft aria-hidden="true" /> Serien</Link>
        <span>Säsong {season}</span>
      </div>
      <div className="native-v3-team-profile-main">
        <TeamLogo teamName={compactTeamName(team.name)} size={82} fallback={<Shield aria-hidden="true" />} />
        <div>
          <span className="native-v3-series-kicker">Lagsida</span>
          <h1 className="native-v3-display">{compactTeamName(team.name)}</h1>
          <p>{team.city || team.arena || "Bauhausligan"}</p>
        </div>
        <div className="native-v3-team-position">
          <strong className="native-v3-display">#{standing?.position ?? "–"}</strong>
          <small>{standing?.points ?? 0} poäng</small>
        </div>
      </div>
      <div className="native-v3-team-stat-grid">
        <div><MapPin aria-hidden="true" /><span><small>Ort</small><strong>{team.city || "Saknas"}</strong></span></div>
        <div><Shield aria-hidden="true" /><span><small>Arena</small><strong>{team.arena || "Saknas"}</strong></span></div>
        <div><CalendarDays aria-hidden="true" /><span><small>Matcher</small><strong>{standing?.played ?? matchSummary.finished.length}</strong></span></div>
        <div><GitCompareArrows aria-hidden="true" /><span><small>Poängskillnad</small><strong>{scored}/{conceded} <em data-tone={diff < 0 ? "negative" : "positive"}>({diff > 0 ? `+${diff}` : diff})</em></strong></span></div>
        <button type="button" onClick={onRoster}><Users aria-hidden="true" /><span><small>Trupp</small><strong>{riderCount}</strong></span><ChevronRight aria-hidden="true" /></button>
      </div>
    </header>
  );
}

function TeamNextMatch({ team, match, onOpenMatch }) {
  if (!match) {
    return <p className="native-v3-series-message">Ingen kommande match är inplanerad för laget.</p>;
  }
  const home = teamName(match, "home");
  const away = teamName(match, "away");
  return (
    <button
      type="button"
      className="native-v3-team-next-match"
      onClick={() => onOpenMatch(match.id || match._id)}
      aria-label={`Öppna matchkort för ${home} mot ${away}`}
    >
      <div className="native-v3-series-track" aria-hidden="true" />
      <div className="native-v3-team-next-meta">
        <span>{isTeamHome(match, team) ? "Hemmamatch" : "Bortamatch"}</span>
        <small>{formatSvDate(match.date, { weekday: "short", day: "numeric", month: "short" })}</small>
      </div>
      <div className="native-v3-team-next-versus">
        <span><TeamLogo teamName={home} size={54} /><strong className="native-v3-display">{home}</strong></span>
        <div><b>VS</b><small><Clock3 aria-hidden="true" />{formatSvTime(match.date, { hour: "2-digit", minute: "2-digit" })}</small></div>
        <span><TeamLogo teamName={away} size={54} /><strong className="native-v3-display">{away}</strong></span>
      </div>
      <span className="native-v3-team-next-action">Se matchkort <ChevronRight aria-hidden="true" /></span>
    </button>
  );
}

function TeamRecentMatches({ team, matches }) {
  const batchSize = 5;
  const loadTimerRef = React.useRef(null);
  const [visibleCount, setVisibleCount] = React.useState(batchSize);
  const [loadingMore, setLoadingMore] = React.useState(false);

  React.useEffect(() => {
    setVisibleCount(batchSize);
    setLoadingMore(false);
    if (loadTimerRef.current) window.clearTimeout(loadTimerRef.current);
    loadTimerRef.current = null;
  }, [matches, team?.id]);

  React.useEffect(() => () => {
    if (loadTimerRef.current) window.clearTimeout(loadTimerRef.current);
  }, []);

  if (!matches.length) return <p className="native-v3-series-message">Det finns inga avslutade matcher att visa ännu.</p>;

  const visibleMatches = matches.slice(0, visibleCount);
  const hasMore = visibleCount < matches.length;
  const loadMore = () => {
    if (!hasMore || loadingMore || loadTimerRef.current) return;
    setLoadingMore(true);
    loadTimerRef.current = window.setTimeout(() => {
      setVisibleCount((current) => Math.min(current + batchSize, matches.length));
      setLoadingMore(false);
      loadTimerRef.current = null;
    }, 180);
  };
  const handleScroll = (event) => {
    const rail = event.currentTarget;
    const distanceToEnd = rail.scrollWidth - rail.clientWidth - rail.scrollLeft;
    if (distanceToEnd <= Math.max(120, rail.clientWidth * 0.45)) loadMore();
  };

  return (
    <div className="native-v3-team-results-rail" onScroll={handleScroll}>
      {visibleMatches.map((match) => {
        const outcome = outcomeFor(match, team);
        const opponent = opponentName(match, team);
        return (
          <article key={match.id || `${match.home_team}-${match.away_team}-${match.date}`} className="native-v3-team-result">
            <div className="native-v3-team-result-top">
              <span>{formatSvDate(match.date, { day: "numeric", month: "short" })}</span>
              <i data-result={String(outcome || "").toLowerCase()}>{outcome}</i>
            </div>
            <strong className="native-v3-display">{match.home_score}<small>–</small>{match.away_score}</strong>
            <div><TeamLogo teamName={opponent} size={25} /><span><b>{opponent}</b><small>{isTeamHome(match, team) ? "Hemmamatch" : "Bortamatch"}</small></span></div>
          </article>
        );
      })}
      {hasMore ? (
        <div className="native-v3-team-results-loader" aria-label="Laddar fler matcher">
          <LoaderCircle className={loadingMore ? "animate-spin" : ""} aria-hidden="true" />
        </div>
      ) : null}
    </div>
  );
}

function TeamRoster({ riders, season, rosterRef }) {
  return (
    <section ref={rosterRef} className="native-v3-team-roster">
      <div className="native-v3-series-section-title">
        <span>Laguppställning</span>
        <h2 className="native-v3-display">Trupp {season}</h2>
      </div>
      {riders.length ? (
        <div className="native-v3-team-roster-list">
          {riders.map((rider, index) => (
            <div key={rider.id} className="native-v3-team-rider-row">
              <span className="native-v3-team-rider-index">{String(index + 1).padStart(2, "0")}</span>
              <span className="native-v3-team-rider-name"><strong>{rider.name}</strong><RiderFlag countryCode={rider.country_code} /></span>
              <span className="native-v3-team-rider-average"><small>Ingångssnitt</small><strong>{formatAverage(rider.entryAvg)}</strong></span>
            </div>
          ))}
        </div>
      ) : <p className="native-v3-series-message">Inga förare är registrerade för laget denna säsong.</p>}
    </section>
  );
}

function TeamDetail({ team, standing, roster, season, matchSummary, loading, rosterError, onOpenMatch }) {
  const rosterRef = React.useRef(null);
  const riders = React.useMemo(() => [...(roster?.mains || []), ...(roster?.reserves || [])]
    .sort((first, second) => String(first?.name || "").localeCompare(String(second?.name || ""), "sv")), [roster]);

  if (loading) return <NativeSeriesSkeleton detail />;
  if (!team) return <p className="native-v3-series-message">Laget kunde inte hittas.</p>;

  const scrollToRoster = () => {
    const element = rosterRef.current;
    if (!element) return;
    window.scrollTo({ top: Math.max(0, element.getBoundingClientRect().top + window.scrollY - 88), behavior: "smooth" });
  };

  return (
    <div className="native-v3-team-page">
      <TeamProfile team={team} standing={standing} season={season} matchSummary={matchSummary} riderCount={riders.length} onRoster={scrollToRoster} />

      <section className="native-v3-team-section">
        <div className="native-v3-series-section-title"><span>Matchläge</span><h2 className="native-v3-display">Nästa match</h2></div>
        <TeamNextMatch team={team} match={matchSummary.nextMatch} onOpenMatch={onOpenMatch} />
      </section>

      <section className="native-v3-team-section">
        <div className="native-v3-series-section-title"><span>Form</span><h2 className="native-v3-display">Senaste matcher</h2></div>
        <TeamRecentMatches team={team} matches={matchSummary.recentMatches} />
      </section>

      {rosterError ? <p className="native-v3-series-message">Kunde inte ladda lagets förare just nu.</p> : null}
      <TeamRoster riders={riders} season={season} rosterRef={rosterRef} />
    </div>
  );
}

export default function NativeSeriesPage({
  season,
  teamId,
  teams,
  teamsLoading,
  teamsError,
  selectedTeam,
  selectedStanding,
  roster,
  rosterError,
  matchSummary,
  detailLoading,
  onOpenMatch,
}) {
  if (!teamId) {
    return <SeriesOverview season={season} teams={teams} loading={teamsLoading} error={teamsError} />;
  }

  return (
    <TeamDetail
      team={selectedTeam}
      standing={selectedStanding}
      roster={roster}
      season={season}
      matchSummary={matchSummary}
      loading={detailLoading}
      rosterError={rosterError}
      onOpenMatch={onOpenMatch}
    />
  );
}
